import React, { useState, useEffect } from 'react';

/**
 * Hook to track screen resolution and check if it meets minimum requirements
 * @param {number} minWidth - Minimum screen width in pixels (default: 768)
 * @returns {object} - { width, height, meetsMinimum, isBelowThreshold }
 */
export const useScreenResolution = (minWidth = 768) => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial dimensions
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const meetsMinimum = dimensions.width >= minWidth;
  const isBelowThreshold = !meetsMinimum;

  return {
    width: dimensions.width,
    height: dimensions.height,
    meetsMinimum,
    isBelowThreshold,
    minWidth,
  };
};

/**
 * Component to block access if screen resolution is below threshold
 */
export const ResolutionGuard = ({ 
  children, 
  minWidth = 768, 
  fallbackComponent = null,
  showMessage = true 
}) => {
  const { meetsMinimum, width, minWidth: threshold } = useScreenResolution(minWidth);

  if (!meetsMinimum) {
    if (fallbackComponent) {
      return fallbackComponent;
    }

    if (showMessage) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: '"Red Hat Display", sans-serif',
          background: '#f8fafc',
          padding: '20px',
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '500px',
            padding: '40px',
            background: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '20px',
            }}>📱</div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#263238',
              marginBottom: '12px',
            }}>
              Screen Size Recommendation
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#607d8b',
              marginBottom: '16px',
              lineHeight: '1.6',
            }}>
              For a better experience setting up your clinic, please open this page on a bigger screen.
            </p>
            <p style={{
              fontSize: '14px',
              color: '#90a4ae',
              marginBottom: '8px',
            }}>
              Recommended minimum screen width: <strong>{threshold}px</strong>
            </p>
            <p style={{
              fontSize: '14px',
              color: '#90a4ae',
            }}>
              Your current screen width: <strong>{width}px</strong>
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  return children;
};

